import { ref, watch } from 'vue'
import type { AuthUser, Student } from '@/types/user';
import { useFirebaseAuth } from '~/composables/useFirebaseAuth'
import { useRequestSecurity } from '~/composables/useRequestSecurity'

const isLoading = ref(false)
const error = ref<string | null>(null)

type State = {
  user: Maybe<AuthUser>,
  student: Maybe<Student>
}

export const useAuthFirebase = () => {
  const hasSession = useCookie("XSRF-TOKEN", {
    maxAge: undefined,
    expires: undefined
  });
  const { 
    currentUser: firebaseUser, 
    isAuthenticated: isFirebaseAuthenticated,
    signIn: firebaseSignIn,
    signUp: firebaseSignUp,
    signOut: firebaseSignOut,
    getIdToken,
    initAuthStateListener
  } = useFirebaseAuth()

  const state = useState<State>('auth.state', () => ({
    user: null,
    student: null
  }))

  const authUser = computed<AuthUser | null>({
    get: (): AuthUser | null => {
      return state.value.user ?? null
    },
    set: (value: AuthUser | null) => {
      state.value.user = value ?? null
    }
  })

  const isAuthenticated = computed(() => {
    return !!(authUser.value && (isFirebaseAuthenticated.value || hasSession.value))
  })

  function validateSession() {
    if (hasSession.value) return
    
    return $api("/sanctum/csrf-cookie");
  };

  async function setUser(user: AuthUser | null, student: Student | null = null) {
    const authId = useCookie("auth-id", {
      maxAge: undefined,
      expires: undefined
    });

    state.value.user = user;
    state.value.student = student;
    authId.value = user?.id?.toString()
  };

  // Fetch current user data from API
  const fetchCurrentUser = async () => {
    const { $api } = useNuxtApp()
    const response = await $api('/api/me') as any
    
    if (!response || !response.data) {
      throw new Error('Failed to fetch current user: Invalid response')
    }
    
    await setUser(response.data, null)
    return response.data
  };

  // Firebase Authentication Methods
  const signUpWithFirebase = async (email: string, password: string, displayName?: string, redirectTo: string = '/dashboard', nameFields?: { first_name: string, middle_name?: string, last_name: string }) => {
    isLoading.value = true
    error.value = null
  
    try {
      // Sign up with Firebase
      const firebaseAuthUser = await firebaseSignUp(email, password, displayName)
      
      if (!firebaseAuthUser) {
        throw new Error('Firebase sign up failed')
      }

      // Get Firebase ID token
      const idToken = await getIdToken()
      if (!idToken) {
        throw new Error('Failed to get Firebase ID token')
      }

      // Authenticate with backend using Firebase token
      const response = await $postApi<any>("/api/firebase/authenticate", {
        body: { 
          id_token: idToken,
          name_fields: nameFields // Pass name fields for proper user creation
        },
      }).catch((err: any) => {
        throw err
      });
      
      // Check if MFA is required
      if (response.requires_mfa) {
        return {
          requiresMfa: true,
          user: response.user,
          firebaseUid: response.firebase_uid
        }
      }

      // Handle response structure for successful authentication
      let user: AuthUser & { student?: Student }
      
      if (response.data && response.data.user) {
        user = response.data.user
      } else if (response.user) {
        user = response.user
      } else {
        throw new Error('Invalid response structure from Firebase authentication API')
      }

      // Extract student data from user object if it exists
      const studentData = user?.student
      await setUser(user ?? null, studentData ?? null);
      
      try {
        await navigateTo(redirectTo)
      } catch (navError) {
        if (process.client) {
          window.location.href = redirectTo
        }
      }
  
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'An error occurred during Firebase sign up'
      throw error.value
    } finally {
      isLoading.value = false
    }
  }

  const loginWithFirebase = async (email: string, password: string, redirectTo: string = '/dashboard') => {
    isLoading.value = true
    error.value = null
    let firebaseAuthUser: any = null
  
    try {
      // Sign in with Firebase
      firebaseAuthUser = await firebaseSignIn(email, password)
      
      if (!firebaseAuthUser) {
        throw new Error('Firebase authentication failed')
      }

      // Get Firebase ID token
      const idToken = await getIdToken()
      if (!idToken) {
        throw new Error('Failed to get Firebase ID token')
      }

      // Authenticate with backend using Firebase token
      const response = await $postApi<any>("/api/firebase/authenticate", {
        body: { id_token: idToken },
        credentials: 'include',
      }).catch((err: any) => {
        throw err
      });

      // Check if MFA is required before processing user data
      if (response.requires_mfa) {
        // Store minimal temporary auth data for 2FA verification
        if (import.meta.client) {
          const tempSession = useSessionStorage<{
            firebaseUid: string,
            redirectTo: string,
            timestamp: number,
            userId: string,
            email: string
          }>('auth.temp', {
            firebaseUid: '',
            redirectTo: '',
            timestamp: 0,
            userId: '',
            email: ''
          });
          tempSession.value = {
            firebaseUid: response.firebase_uid || firebaseAuthUser.uid,
            redirectTo,
            timestamp: Date.now(),
            userId: '',
            email: firebaseAuthUser.email || ''
          };
        }
        
        return {
          requiresMfa: true,
          user: {
            email: firebaseAuthUser.email || '',
            mfa_enabled: response.user?.mfa_enabled || true,
            mfa_verified: false
          },
          firebaseUid: response.firebase_uid || firebaseAuthUser.uid
        }
      }
      
      // Handle response structure for successful authentication
      let user: AuthUser & { student?: Student }
      
      if (response.data && response.data.user) {
        user = response.data.user
      } else if (response.user) {
        user = response.user
      } else {
        throw new Error('Invalid response structure from Firebase authentication API')
      }

      // Extract student data from user object if it exists
      const studentData = user?.student
      await setUser(user ?? null, studentData ?? null);
      
      try {
        await navigateTo(redirectTo)
      } catch (navError) {
        // Fallback to window.location if navigateTo fails
        if (process.client) {
          window.location.href = redirectTo
        }
      }
  
    } catch (err: any) {
      const mfaResponse = err?.data ?? err?.response?.data
      if (mfaResponse?.requires_mfa) {
        const firebaseUid = mfaResponse.firebase_uid || firebaseAuthUser?.uid || ''
        if (import.meta.client) {
          const tempSession = useSessionStorage<{
            firebaseUid: string,
            redirectTo: string,
            timestamp: number,
            userId: string,
            email: string
          }>('auth.temp', {
            firebaseUid: '',
            redirectTo: '',
            timestamp: 0,
            userId: '',
            email: ''
          });
          tempSession.value = {
            firebaseUid,
            redirectTo,
            timestamp: Date.now(),
            userId: '',
            email: firebaseAuthUser?.email || ''
          };
        }
        return {
          requiresMfa: true,
          user: {
            email: firebaseAuthUser?.email || '',
            mfa_enabled: true,
            mfa_verified: false
          },
          firebaseUid
        }
      }
      error.value = err instanceof Error ? err.message : 'An error occurred during Firebase login'
      throw error.value
    } finally {
      isLoading.value = false
    }
  }

  // Complete 2FA login after verification
  const completeMfaLogin = async (firebaseUid: string, code: string) => {
    isLoading.value = true
    error.value = null

    try {
      const { verifyLoginCode } = useMfaLogin()
      
      const verificationResponse = await verifyLoginCode(firebaseUid, code)
      const verificationToken = verificationResponse.verification_token
      
      if (!verificationToken) {
        throw new Error('Verification token not received')
      }
      
      // Get the stored temporary data from session storage
      const tempSession = useSessionStorage<{
        firebaseUid: string,
        redirectTo: string,
        timestamp: number,
        userId: string,
        email: string
      }>('auth.temp', {
        firebaseUid: '',
        redirectTo: '/dashboard',
        timestamp: 0,
        userId: '',
        email: ''
      });
      const redirectTo = tempSession.value?.redirectTo || '/dashboard'
      
      // Get fresh Firebase token
      const idToken = await getIdToken()
      if (!idToken) {
        throw new Error('Failed to get Firebase ID token')
      }

      const response = await $postApi<any>("/api/firebase/authenticate", {
        body: { id_token: idToken },
        credentials: 'include',
        headers: {
          'X-MFA-Verification-Token': verificationToken
        }
      }).catch((err: any) => {
        const errorData = err?.data || err?.response?.data || {}
        if (errorData.requires_mfa || errorData.message === 'MFA verification required') {
          throw new Error('MFA verification required. Please verify your code again.')
        }
        throw err
      })

      if (response.requires_mfa || response.message === 'MFA verification required') {
        throw new Error('MFA verification required. Please verify your code again.')
      }

      if (!response || (!response.data && !response.user)) {
        throw new Error('Invalid authentication response from server')
      }

      if (!response.custom_token && !response.data?.custom_token) {
        throw new Error('Authentication failed: Missing authentication token')
      }

      let user: AuthUser & { student?: Student }
      
      if (response.data && response.data.user) {
        user = response.data.user
      } else if (response.user) {
        user = response.user
      } else {
        throw new Error('Invalid response structure from Firebase authentication API')
      }

      if (!user || !user.id) {
        throw new Error('Invalid user data received from server')
      }

      if (user.mfa_enabled && !user.mfa_verified) {
        throw new Error('MFA verification required. Please verify your code again.')
      }

      const studentData = user?.student
      await setUser(user ?? null, studentData ?? null)

      // Clean up temporary data using secure session storage
      if (import.meta.client) {
        const tempSession = useSessionStorage('auth.temp', null);
        tempSession.value = null;
      }

      try {
        await navigateTo(redirectTo)
      } catch (navError) {
        // Fallback to window.location if navigateTo fails
        if (process.client) {
          window.location.href = redirectTo
        }
      }

    } catch (err) {
      error.value = err instanceof Error ? err.message : 'An error occurred during 2FA verification'
      throw err // Throw the original error to preserve data, status, etc.
    } finally {
      isLoading.value = false
    }
  }

  const login = async () => {
    throw new Error('Email/password login is disabled. Use Firebase with OTP.')
  }

  const logout = async () => {
    isLoading.value = true
    try {
      try {
        await $postApi("/api/logout")
      } catch (logoutError) {
        console.error('Backend logout failed:', logoutError)
      }

      if (isFirebaseAuthenticated.value) {
        try {
          await firebaseSignOut()
        } catch (firebaseError) {
          console.error('Firebase sign out failed:', firebaseError)
        }
      }

      reset()
      
      await navigateTo('/login', { replace: true })

    } catch (err) {
      error.value = err instanceof Error ? err.message : 'An error occurred during logout'
      reset()
      await navigateTo('/login', { replace: true })
    } finally {
      isLoading.value = false
    }
  }

  function reset() {
    const showMessage = useCookie<boolean>('show-welcome-message', {
      maxAge: undefined,
      expires: undefined
    });
    const authId = useCookie("auth-id", {
      maxAge: undefined,
      expires: undefined
    });

    clearNuxtData()
    setUser(null)
    hasSession.value = null
    authId.value = null
    showMessage.value = false
    
    if (process.client) {
      const { setSessionSecret } = useRequestSecurity()
      setSessionSecret(null as any)
    }
  }

  // Initialize Firebase auth state listener
  const initAuth = () => {
    if (process.client) {
      initAuthStateListener()
    }
  }

  return {
    ...toRefs(state.value), // Update to use .value since we're using useState
    isAuthenticated,
    isLoading,
    error,
    login,
    loginWithFirebase,
    completeMfaLogin,
    signUpWithFirebase,
    logout,
    authUser,
    setUser,
    fetchCurrentUser,
    initAuth,
    // Firebase specific
    firebaseUser,
    isFirebaseAuthenticated,
  }
}
